export interface ScorecardTable {
  title: string
  rowHeader: string
  colHeaders: string[]
  rows: Array<{ label: string; values: string[] }>
}

export function splitCsvLine(line: string, delimiter: string) {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      const next = line[i + 1]
      if (inQuotes && next === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (!inQuotes && ch === delimiter) {
      result.push(current.trim())
      current = ''
      continue
    }

    current += ch
  }

  result.push(current.trim())
  return result.map((v) => v.replace(/^"|"$/g, '').trim())
}

export function parseWhistScorecardCsv(csvText: string): ScorecardTable[] {
  const text = (csvText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = text.split('\n')
  if (lines.length === 0) return []

  // Filen i projektet bruger semikolon
  const delimiter = ';'
  const grid = lines.map((line) => splitCsvLine(line, delimiter))

  const isEmptyRow = (row: string[]) => row.every((c) => (c || '').trim().length === 0)
  const trimTrailingEmpty = (row: string[]) => {
    let end = row.length
    while (end > 0 && (row[end - 1] || '').trim().length === 0) end--
    return row.slice(0, end)
  }

  const blocks: string[][][] = []
  let current: string[][] = []

  for (const rawRow of grid) {
    const row = trimTrailingEmpty(rawRow)
    if (row.length === 0 || isEmptyRow(row)) {
      if (current.length > 0) {
        blocks.push(current)
        current = []
      }
      continue
    }
    current.push(row)
  }
  if (current.length > 0) blocks.push(current)

  const tables: ScorecardTable[] = []
  for (const block of blocks) {
    if (block.length === 0) continue
    const headerRow = block[0]
    const title = (headerRow[0] || '').trim()
    const colHeaders = headerRow.slice(1).map((h) => (h || '').trim()).filter((h) => h.length > 0)
    if (!title) continue
    if (colHeaders.length === 0) continue

    const dataRows = block.slice(1)
      .filter((r) => (r[0] || '').trim().length > 0)
      .map((r) => {
        const label = (r[0] || '').trim()
        const values = r.slice(1, 1 + colHeaders.length).map((v) => (v || '').trim())
        return { label, values }
      })
      .filter((r) => r.values.some((v) => v.length > 0))

    if (dataRows.length === 0) continue

    const labelsAsInts = dataRows.map((r) => Number.parseInt(r.label, 10)).filter((n) => Number.isFinite(n))
    const isTricksTable = labelsAsInts.length === dataRows.length && labelsAsInts.every((n) => n >= 0 && n <= 13)
    const rowHeader = isTricksTable ? 'Stik' : 'Spil'

    tables.push({ title, rowHeader, colHeaders, rows: dataRows })
  }

  // Dedupe identiske tabeller (CSV'en kan indeholde dubletter)
  const seen = new Set<string>()
  const deduped: ScorecardTable[] = []
  for (const t of tables) {
    const key = JSON.stringify({ title: t.title, colHeaders: t.colHeaders, rows: t.rows })
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(t)
  }

  return deduped
}
